import { FilesetResolver, PoseLandmarker, HandLandmarker, type Landmark, type NormalizedLandmark } from '@mediapipe/tasks-vision';
import { forEachVideoFrame, type CropRect, type CropController } from './videoFrames';

/**
 * MediaPipe Tasks Vision の wasm 一式は vite-plugin-static-copy で
 * `mediapipe/wasm/` に配信している（vite.config.ts 参照）。base 配下に
 * 配信されるため import.meta.env.BASE_URL を前置する。
 */
const WASM_BASE_URL = `${import.meta.env.BASE_URL}mediapipe/wasm`;

/**
 * ポーズ検出モデル本体（float16, heavy モデルは約29MB）。リポジトリには同梱せず、
 * 初回利用時に Google の公式 CDN から取得する。"latest" ではなくバージョン
 * 番号付き URL を使い、モデルの差し替えでポーズ推定の挙動が変わらないよう固定する。
 * full から heavy に変更し、動画1本から静止ポーズの精度を上げるための検出品質を優先する。
 * VRMA 生成の本番品質を決める録画後解析（estimatePosesFromVideo）専用。ライブプレビューは
 * 下記 LIVE_MODEL_ASSET_URL（lite）を使う別インスタンスなので、こちらは変更しない。
 */
const MODEL_ASSET_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task';

/**
 * ライブプレビュー（録画画面の骨格オーバーレイ）専用のポーズ検出モデル本体（float16,
 * lite モデル）。heavy より大幅に軽く、録画中でも実機である程度のfpsで動かせる。
 * 精度より軽さを優先する用途（骨格が拾えているかの確認）にのみ使い、生成品質を左右する
 * 録画後解析（MODEL_ASSET_URL / heavy）とは混同しない。
 */
const LIVE_MODEL_ASSET_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

/**
 * 手指ランドマーク検出モデル本体。PoseLandmarker とは別モデル・別インスタンスで、
 * 録画後解析（estimatePosesFromVideo）でのみ使う。クロップ済みフレーム（手が相対的に
 * 大きく写る）に対して同じフレームループ内で追加検出する。
 */
const HAND_MODEL_ASSET_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

/** 検出前に縮小するフレーム画像の長辺の上限（px）。大きすぎる画像の推定を軽くするため。 */
const MAX_IMAGE_DIMENSION = 1280;

/**
 * 動画から走査するフレーム数の上限。heavy モデルでの逐次推論は1フレームあたり数百ms
 * かかりうるため、多く取りすぎるとブラウザが長時間固まる。静止ポーズ抽出には
 * 数秒分を数fpsで拾えれば十分なため、90フレーム（15fps 換算で約6秒分）を上限とする。
 */
const MAX_VIDEO_FRAMES = 90;

/** クロップ追従: 有効(visibility>0.5)なランドマークがこの点数未満なら bbox を作らずフォールバックする。 */
const CROP_MIN_VISIBLE_LANDMARKS = 4;
/** クロップ追従: bbox に使うランドマークの visibility しきい値。 */
const CROP_VISIBILITY_THRESHOLD = 0.5;
/** クロップ追従: bbox の上下左右に付ける余白（bbox 幅・高さに対する割合）。 */
const CROP_MARGIN_RATIO = 0.2;
/** クロップ追従: 極端に小さいクロップにならないよう確保する最小の幅・高さ（動画正規化座標）。 */
const CROP_MIN_SIZE = 0.1;

/** 鮮明度（ラプラシアン分散）を計算する際に画像を間引く長辺の上限（px）。 */
const SHARPNESS_MAX_DIMENSION = 256;

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * 検出結果の landmarks2d（そのフレームのクロップ座標系）から、次フレームの人物クロップ範囲
 * （動画正規化座標）を求める。有効な bbox が得られない場合は null を返し、呼び出し側は
 * 次フレームを全体検出にフォールバックする。
 * currentCropRect は今回の検出に実際に使ったクロップ範囲（frame.cropRect。全体なら null）。
 * landmarks2d はこの currentCropRect が張る座標系の 0〜1 正規化点なので、
 * 動画正規化座標へは (currentCropRect.x + lx*currentCropRect.w, ...) で写像する。
 */
function computeNextCropRect(
  landmarks2d: NormalizedLandmark[] | undefined,
  currentCropRect: CropRect | null,
): CropRect | null {
  if (!landmarks2d) return null;
  const visible = landmarks2d.filter((lm) => (lm.visibility ?? 0) > CROP_VISIBILITY_THRESHOLD);
  if (visible.length < CROP_MIN_VISIBLE_LANDMARKS) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const lm of visible) {
    minX = Math.min(minX, lm.x);
    maxX = Math.max(maxX, lm.x);
    minY = Math.min(minY, lm.y);
    maxY = Math.max(maxY, lm.y);
  }
  minX = clamp01(minX);
  maxX = clamp01(maxX);
  minY = clamp01(minY);
  maxY = clamp01(maxY);

  const region = currentCropRect ?? { x: 0, y: 0, w: 1, h: 1 };
  let vx0 = region.x + minX * region.w;
  let vx1 = region.x + maxX * region.w;
  let vy0 = region.y + minY * region.h;
  let vy1 = region.y + maxY * region.h;

  const marginX = (vx1 - vx0) * CROP_MARGIN_RATIO;
  const marginY = (vy1 - vy0) * CROP_MARGIN_RATIO;
  vx0 -= marginX;
  vx1 += marginX;
  vy0 -= marginY;
  vy1 += marginY;

  if (vx1 - vx0 < CROP_MIN_SIZE) {
    const cx = (vx0 + vx1) / 2;
    vx0 = cx - CROP_MIN_SIZE / 2;
    vx1 = cx + CROP_MIN_SIZE / 2;
  }
  if (vy1 - vy0 < CROP_MIN_SIZE) {
    const cy = (vy0 + vy1) / 2;
    vy0 = cy - CROP_MIN_SIZE / 2;
    vy1 = cy + CROP_MIN_SIZE / 2;
  }

  vx0 = clamp01(vx0);
  vx1 = clamp01(vx1);
  vy0 = clamp01(vy0);
  vy1 = clamp01(vy1);
  if (vx1 - vx0 < 1e-6 || vy1 - vy0 < 1e-6) return null;

  return { x: vx0, y: vy0, w: vx1 - vx0, h: vy1 - vy0 };
}

/**
 * フレーム画像の鮮明度スコア（ラプラシアンの分散）を計算する。値が小さいほどぼやけている
 * （モーションブラー等）と判断できる。重い解像度で直接評価すると負荷が大きいため、
 * 必要なら長辺 SHARPNESS_MAX_DIMENSION px まで間引いてから評価する。
 */
function computeSharpness(canvas: OffscreenCanvas | HTMLCanvasElement): number {
  const srcWidth = canvas.width;
  const srcHeight = canvas.height;
  const scale = Math.min(1, SHARPNESS_MAX_DIMENSION / Math.max(srcWidth, srcHeight));
  const width = Math.max(1, Math.round(srcWidth * scale));
  const height = Math.max(1, Math.round(srcHeight * scale));

  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (scale < 1) {
    let tmp: OffscreenCanvas | HTMLCanvasElement;
    if (typeof OffscreenCanvas !== 'undefined') {
      tmp = new OffscreenCanvas(width, height);
    } else {
      const htmlTmp = document.createElement('canvas');
      htmlTmp.width = width;
      htmlTmp.height = height;
      tmp = htmlTmp;
    }
    const tmpCtx = tmp.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!tmpCtx) return 0;
    tmpCtx.drawImage(canvas, 0, 0, width, height);
    ctx = tmpCtx;
  } else {
    ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  }
  if (!ctx) return 0;

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  const lap: number[] = [];
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const value = gray[idx - width] + gray[idx + width] + gray[idx - 1] + gray[idx + 1] - 4 * gray[idx];
      lap.push(value);
    }
  }
  if (lap.length === 0) return 0;
  const mean = lap.reduce((a, b) => a + b, 0) / lap.length;
  const variance = lap.reduce((a, b) => a + (b - mean) ** 2, 0) / lap.length;
  return variance;
}

/**
 * 1フレームで検出できた片手分のランドマーク。body とは異なり多視点融合はせず、
 * サイド（左右）ごとにスコア最良の1フレームをそのまま採用する（poseFusion.ts 参照）。
 */
export interface HandSample {
  /** MediaPipe の handedness 分類（'Left' | 'Right'）。 */
  side: 'Left' | 'Right';
  /** handedness 分類のスコア（0〜1）。サイドごとの最良フレーム選定に使う。 */
  score: number;
  /** 手首を原点としたメートル座標系の21点（world landmarks）。 */
  worldLandmarks: Landmark[];
  /** 画像座標系（0〜1正規化）の21点。Kalidokit.Hand.solve への入力に使う。 */
  landmarks: NormalizedLandmark[];
}

export interface PoseSample {
  /** 画像座標系（0〜1正規化）の33点。visibility を含む。 */
  landmarks2d: NormalizedLandmark[];
  /** 腰中心のメートル座標系の33点（world landmarks）。 */
  worldLandmarks: Landmark[];
  /** 推定に使ったフレーム画像の幅・高さ（縮小後、px）。 */
  imageWidth: number;
  imageHeight: number;
  /** 動画内の時刻（ms）。 */
  timestampMs: number;
  /** 鮮明度スコア（ラプラシアン分散）。値が小さいほどぼやけている。 */
  sharpness: number;
  /** 同フレームで検出できた左手（あれば）。手が写っていないフレームでは undefined。 */
  leftHand?: HandSample;
  /** 同フレームで検出できた右手（あれば）。 */
  rightHand?: HandSample;
}

// PoseLandmarker の生成は重い（wasm 初期化 + モデルダウンロード）ので、
// 並行して複数回呼ばれても一度しか初期化しないようにモジュール内でシングルトン化する。
let landmarkerPromise: Promise<PoseLandmarker> | null = null;

/**
 * VIDEO モードの PoseLandmarker は、インスタンスの生存期間を通じて detectForVideo に
 * 渡すタイムスタンプが単調増加であることを要求する。landmarker はページ内でシングルトン
 * として使い回される一方、forEachVideoFrame が返す frame.timestampMs は実行のたびに
 * その動画の 0ms から始まる。そのため2回目以降の推定実行では、1回目最後のタイムスタンプ
 * より小さい値（0ms）を detectForVideo に渡すことになり、MediaPipe 側でタイムスタンプの
 * 逆行として拒否されグラフが停止する（＝2回目の推定がハングするバグの原因）。
 * これを防ぐため、実行をまたいでも detectForVideo に渡す値だけは増え続けるよう、
 * 直近に渡した値をここで記憶しておく。PoseSample.timestampMs（動画内の時刻。
 * poseFusion.ts が参照する）にはこのオフセットを混ぜない。
 * このカウンタは録画後解析（estimatePosesFromVideo）専用。ライブ検出は別インスタンス
 * （liveLandmarkerPromise）を使うため、下記 lastLiveTimestampMs / nextLiveTimestampMs
 * という別カウンタで管理する。
 */
let lastVideoTimestampMs = 0;

/**
 * detectForVideo（録画後解析用 landmarker）に渡す単調増加タイムスタンプを算出するヘルパー。
 * lastVideoTimestampMs 周辺のコメントの通り、VIDEO モードの PoseLandmarker は
 * インスタンスの生存期間を通じてタイムスタンプの逆行を許さない。常に
 * 「desired 以上かつ前回より必ず大きい」値を返す。
 */
export function nextVideoTimestampMs(desiredMs: number): number {
  const ts = Math.max(lastVideoTimestampMs + 1, desiredMs);
  lastVideoTimestampMs = ts;
  return ts;
}

export function getPoseLandmarker(): Promise<PoseLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
      return PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MODEL_ASSET_URL,
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
      });
    })().catch((e) => {
      // 初期化に失敗したら次回また作り直せるようにキャッシュを捨てる。
      landmarkerPromise = null;
      throw e;
    });
  }
  return landmarkerPromise;
}

// ライブプレビュー専用の PoseLandmarker シングルトン（lite モデル）。録画後解析用の
// landmarkerPromise（heavy）とはインスタンスを分ける。理由は2つ: (1) プレビューの軽さと
// 生成品質を左右する解析の精度はトレードオフの関係にあり、両立させるには別々のモデル・
// インスタンスが要る。(2) VIDEO モードの detectForVideo はインスタンスごとにタイムスタンプの
// 単調増加を要求するため、そもそも1つのインスタンスをライブ・解析の両方で使い回すこと自体が
// タイムスタンプ管理を難しくする（現に detectLiveLandmarks は以前 landmarkerPromise 側の
// カウンタを間借りしていた）。分離すればライブ側のカウンタ・エラー処理は解析側と完全に独立する。
let liveLandmarkerPromise: Promise<PoseLandmarker> | null = null;

/**
 * VIDEO モードの PoseLandmarker はインスタンスごとにタイムスタンプの単調増加を要求するため、
 * ライブ用インスタンス（liveLandmarkerPromise）にも解析用（lastVideoTimestampMs）とは
 * 独立したカウンタが必要になる。HandLandmarker が別カウンタ（lastHandTimestampMs）を
 * 持っているのと同じ理由・同じ作法。
 */
let lastLiveTimestampMs = 0;

/** detectForVideo（ライブ用 landmarker）に渡す単調増加タイムスタンプを算出する。nextVideoTimestampMs のライブ版。 */
export function nextLiveTimestampMs(desiredMs: number): number {
  const ts = Math.max(lastLiveTimestampMs + 1, desiredMs);
  lastLiveTimestampMs = ts;
  return ts;
}

/**
 * ライブプレビュー専用の PoseLandmarker（lite モデル）を返す。getPoseLandmarker（heavy、
 * 録画後解析専用）とはシングルトンを分けており、互いのタイムスタンプ管理やエラー時の
 * 破棄処理を巻き込まない。
 */
export function getLivePoseLandmarker(): Promise<PoseLandmarker> {
  if (!liveLandmarkerPromise) {
    liveLandmarkerPromise = (async () => {
      const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
      return PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: LIVE_MODEL_ASSET_URL,
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
      });
    })().catch((e) => {
      // 初期化に失敗したら次回また作り直せるようにキャッシュを捨てる。
      liveLandmarkerPromise = null;
      throw e;
    });
  }
  return liveLandmarkerPromise;
}

// HandLandmarker は PoseLandmarker とは別インスタンス・別シングルトンとして管理する
// （wasm 初期化やモデルダウンロードを個別に持つため）。
let handLandmarkerPromise: Promise<HandLandmarker> | null = null;

/**
 * VIDEO モードの HandLandmarker も PoseLandmarker と同様、detectForVideo に渡す
 * タイムスタンプが単調増加であることを要求する。PoseLandmarker とは別インスタンスなので
 * タイムスタンプの単調性も別カウンタで管理する必要があり、lastVideoTimestampMs /
 * nextVideoTimestampMs（pose 用）とは混ぜない。
 */
let lastHandTimestampMs = 0;

/** detectForVideo（HandLandmarker）に渡す単調増加タイムスタンプを算出する。nextVideoTimestampMs の手版。 */
export function nextHandTimestampMs(desiredMs: number): number {
  const ts = Math.max(lastHandTimestampMs + 1, desiredMs);
  lastHandTimestampMs = ts;
  return ts;
}

export function getHandLandmarker(): Promise<HandLandmarker> {
  if (!handLandmarkerPromise) {
    handLandmarkerPromise = (async () => {
      const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
      return HandLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: HAND_MODEL_ASSET_URL,
        },
        runningMode: 'VIDEO',
        numHands: 2,
      });
    })().catch((e) => {
      // 初期化に失敗したら次回また作り直せるようにキャッシュを捨てる。
      handLandmarkerPromise = null;
      throw e;
    });
  }
  return handLandmarkerPromise;
}

/**
 * 動画ファイルからフレームを間引いて取り出し、フレームごとに人物のポーズランドマークを
 * 推定する。VIDEO モードの PoseLandmarker は単調増加するタイムスタンプでの逐次呼び出しが
 * 前提のため、forEachVideoFrame が返す時系列順のフレームをそのまま detectForVideo に渡す。
 * 検出できなかったフレームは読み飛ばし、1件も検出できなくても例外は投げず空配列を返す
 * （呼び出し側が「ポーズを検出できませんでした」等のメッセージを出す設計）。
 */
export async function estimatePosesFromVideo(
  file: File,
  opts?: { onProgress?: (done: number, total: number) => void; signal?: AbortSignal },
): Promise<PoseSample[]> {
  const [landmarker, handLandmarker] = await Promise.all([getPoseLandmarker(), getHandLandmarker()]);
  const samples: PoseSample[] = [];

  // 今回の実行で detectForVideo に渡す値の開始位置。前回実行があれば、その最終値から
  // 1000ms 空けたところから始める（区切りを明確にするための余裕分で、値自体に意味はない）。
  // 初回実行（lastVideoTimestampMs === 0）は動画そのままの 0ms 始まりでよい。
  const timestampOffsetMs = lastVideoTimestampMs > 0 ? lastVideoTimestampMs + 1000 : 0;
  // 手用も同じ考え方でオフセットを求める。pose 用とは別カウンタ（lastHandTimestampMs）なので
  // オフセットも独立して計算する。
  const handTimestampOffsetMs = lastHandTimestampMs > 0 ? lastHandTimestampMs + 1000 : 0;

  // 前フレームの検出結果に応じて次フレームのクロップ範囲を追従させるコントローラ。
  // 初期状態（1フレーム目）は rect なし＝全体検出から始める。
  const cropController: CropController = { rect: null };

  await forEachVideoFrame(
    file,
    { maxFrames: MAX_VIDEO_FRAMES, maxDimension: MAX_IMAGE_DIMENSION, signal: opts?.signal, cropController },
    async (frame, index, total) => {
      // シークの取りこぼし等で理論上は起きないはずだが、同一実行内でも前回渡した値以下に
      // ならないよう nextVideoTimestampMs 経由でガードしてから detectForVideo に渡す。
      const detectTimestampMs = nextVideoTimestampMs(frame.timestampMs + timestampOffsetMs);

      let result;
      try {
        result = landmarker.detectForVideo(frame.canvas, detectTimestampMs);
      } catch (e) {
        // タイムスタンプ不正等でグラフが壊れた場合、そのインスタンスを使い続けても
        // 復帰できない。シングルトンを破棄し、次回の推定実行で作り直せるようにする。
        landmarkerPromise = null;
        throw e;
      }
      // pose 検出に続けて、同じフレーム（クロップ済み canvas なので手も相対的に大きく写る）で
      // 手のランドマークも検出する。単調タイムスタンプは pose とは別カウンタ
      // （nextHandTimestampMs）で管理する。
      let handResult;
      try {
        handResult = handLandmarker.detectForVideo(
          frame.canvas,
          nextHandTimestampMs(frame.timestampMs + handTimestampOffsetMs),
        );
      } catch (e) {
        // pose 側と同様、グラフが壊れた場合はシングルトンを破棄し次回作り直せるようにする。
        handLandmarkerPromise = null;
        throw e;
      }

      try {
        const landmarks2d = result.landmarks[0];
        const worldLandmarks = result.worldLandmarks[0];
        if (landmarks2d && worldLandmarks) {
          // 検出できた手を左右に振り分ける。同じ side が複数出た場合は
          // handedness のスコアが最大のものだけを残す。
          let leftHand: HandSample | undefined;
          let rightHand: HandSample | undefined;
          for (let i = 0; i < handResult.handedness.length; i++) {
            const category = handResult.handedness[i]?.[0];
            const handWorldLandmarks = handResult.worldLandmarks[i];
            const handLandmarks2d = handResult.landmarks[i];
            if (!category || !handWorldLandmarks || !handLandmarks2d) continue;
            if (category.categoryName !== 'Left' && category.categoryName !== 'Right') continue;
            const sample: HandSample = {
              side: category.categoryName,
              score: category.score,
              worldLandmarks: handWorldLandmarks.map((l) => ({ ...l })),
              landmarks: handLandmarks2d.map((l) => ({ ...l })),
            };
            if (sample.side === 'Left') {
              if (!leftHand || sample.score > leftHand.score) leftHand = sample;
            } else {
              if (!rightHand || sample.score > rightHand.score) rightHand = sample;
            }
          }

          samples.push({
            landmarks2d,
            worldLandmarks,
            imageWidth: frame.width,
            imageHeight: frame.height,
            // 動画内の時刻（オフセット無し）。detectForVideo に渡した detectTimestampMs は
            // MediaPipe 都合の値なので、ここには混ぜない。
            timestampMs: frame.timestampMs,
            sharpness: computeSharpness(frame.canvas),
            leftHand,
            rightHand,
          });
        }
        // 次フレーム用のクロップ範囲を今回の検出結果から更新する。有効な bbox が
        // 得られなければ null（全体検出）にフォールバックする。
        cropController.rect = computeNextCropRect(landmarks2d, frame.cropRect);
      } finally {
        result.close();
        // HandLandmarkerResult は PoseLandmarkerResult と異なり内部バッファを持たない
        // ただのデータ interface（close() メソッドが無い）なので、破棄処理は不要。
      }
      opts?.onProgress?.(index + 1, total);
    },
  );

  return samples;
}

/**
 * ライブのカメラ映像1フレームから 2D ランドマーク（0〜1正規化、33点）を推定して返す。
 * 骨格オーバーレイ表示用。検出できなければ null。プレビューは「骨格が拾えているか」の
 * 確認用途であり、精度より軽さを優先して lite モデルの専用インスタンス
 * （getLivePoseLandmarker）を使う。VRMA 生成品質を決める録画後解析（estimatePosesFromVideo）
 * は heavy モデルのまま別インスタンスで動くので、こちらの軽量化の影響を受けない。
 * detectForVideo のタイムスタンプは nextLiveTimestampMs 経由で単調増加を保証する
 * （このライブ用インスタンス専用のカウンタで、解析用の nextVideoTimestampMs とは別物）。
 * landmarker を引数で受け取る形にしているのは、呼び出し側が RAF ループの前に一度
 * getLivePoseLandmarker() を await して保持し、毎フレーム await しないで済むようにするため。
 */
export function detectLiveLandmarks(
  landmarker: PoseLandmarker,
  video: HTMLVideoElement,
  nowMs: number,
): NormalizedLandmark[] | null {
  let result;
  try {
    result = landmarker.detectForVideo(video, nextLiveTimestampMs(nowMs));
  } catch (e) {
    // ライブ用シングルトンのみを破棄する。解析用（landmarkerPromise / heavy）を
    // 巻き添えにしないよう、liveLandmarkerPromise だけをここで捨てる。
    liveLandmarkerPromise = null;
    throw e;
  }
  try {
    const landmarks2d = result.landmarks[0];
    if (!landmarks2d) return null;
    // result.close() で内部バッファが解放されるため、参照を保持したまま返さずコピーする。
    return landmarks2d.map((l) => ({ ...l }));
  } finally {
    result.close();
  }
}
